import { useHistory, Redirect, generatePath } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useState } from 'react'
import { mdiAlert, mdiCheckCircleOutline, mdiPlus } from '@mdi/js'
import { unwrapResult } from '@reduxjs/toolkit'
import { DATE_SHORT, formatTime } from 'app/utils/formatDate'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import routes from 'app/routes'
import { WhiteBlock } from 'components/Layout'
import { useHomeRouteParams } from 'features/home/hooks'
import { LoaderOverlay } from 'components/Loader'

import {
  purchaseRequestSelectors,
  activeSubscriptionSelector,
  subscriptionHomePriceSelector,
  selectedHomeSelector,
} from 'features/home/selectors'
import {
  totalToPayAmountSelector,
  upcomingInvoiceSelectors,
} from '../../selectors'
import { subscriptionManagementMessages } from '../messages'
import { SectionLabel } from 'components/Typography'
import { CommonLinkButton } from 'components/Button'
import { MdiIcon } from 'components/Icons'
import CheckoutInfo from '../CheckoutInfo'
import PromocodeForm from './PromocodeForm'
import { promocodeMessages } from './PromocodeForm/messages'
import {
  StyledUpgradeSubscriptionWrapper,
  StyledCardsBlock,
  StyledAddPromocodeBtn,
  StyledPromocodeRow,
  StyledPaymentMethods,
  PurchaseSuccessIcon,
  PurchaseErrorIcon,
} from './styles'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import {
  defaultPaymentMethodRequestSelectors,
  detachPaymentMethodSelectors,
  paymentMethodsListSelector,
  selectedPaymentMethodIdSelector,
} from 'features/paymentMethods/selectors'
import PaymentMethodsCardsList from 'features/paymentMethods/components/PaymentMethodsCardsList'
import SubsManageBottomPanel from 'components/SubsManageBottomPanel'
import SuccessModal from 'components/Modal/SuccessModal'
import { commonMessages } from 'app/i18n/messages'
import { checkoutInfoMessages } from '../CheckoutInfo/messages'
import { invoiceActions } from '../slice'
import { purchaseSubscriptionThunk } from './slice'
import { subscriptionPeriodEndSelector } from 'features/home'

function UpgradeSubscription() {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { homeId } = useHomeRouteParams()
  const { formatMessage } = useIntl()

  const currentHome = useAppSelector(selectedHomeSelector.data)
  const homeName = currentHome?.name || commonMessages.noName

  const [isOpenPromocodeForm, setIsOpenPromocodeForm] = useState<boolean>(false)
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const [isErrorOpen, setIsErrorOpen] = useState(false)
  const [isAcceptPrivacy, setIsAcceptPrivacy] = useState(false)

  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const price = useAppSelector(subscriptionHomePriceSelector.data)
  const [amountDue, setAmountDue] = useState(0)

  console.log(amountDue === 0 ? '' : ' ')

  const selectedPaymentMethod = useAppSelector(selectedPaymentMethodIdSelector)
  const paymentMethods = useAppSelector(paymentMethodsListSelector.data)
  const upcomingInvoice = useAppSelector(upcomingInvoiceSelectors.data)
  const totalToPayAmount = useAppSelector(
    totalToPayAmountSelector(formatMessage)
  )
  const isPurchaseRequestLoading = useAppSelector(
    purchaseRequestSelectors.isFetching
  )
  const isDefaultPMRequestLoading = useAppSelector(
    defaultPaymentMethodRequestSelectors.isFetching
  )
  const paymentMethodsAreFetching = useAppSelector(
    paymentMethodsListSelector.isFetching
  )

  const paymentMethodsAreFetched = useAppSelector(
    paymentMethodsListSelector.isFinished
  )

  const isPaymentMethodDetaching = useAppSelector(
    detachPaymentMethodSelectors.isFetching
  )
  const isActiveSubscriptionLoading = useAppSelector(
    activeSubscriptionSelector.isFetching
  )

  const addNewCardRedirect = () => {
    if (currentRootId) {
      const addCardPath = generatePath(routes.homeSubscription.addCard(), {
        homeId,
        rootDomainId: currentRootId,
      })
      history.push(addCardPath)
    }
  }

  const handleCancel = () => {
    dispatch(invoiceActions.clear())
    if (currentRootId) {
      const homeStatusPath = generatePath(routes.homeStatus, {
        homeId,
        rootDomainId: currentRootId,
      })
      history.push(homeStatusPath)
    }
  }

  const handleOpenPromocodeForm = () => {
    setIsOpenPromocodeForm(true)
  }

  const handlePurchaseSubscription = async () => {
    try {
      if (!selectedPaymentMethod) {
        throw new Error('No payment method selected')
      }
      if (!currentRootId) {
        throw new Error('No root domain selected')
      }
      await dispatch(
        purchaseSubscriptionThunk({
          paymentMethodId: selectedPaymentMethod,
          homeId,
          rootDomainId: currentRootId,
          type: 'company',
          couponId: upcomingInvoice.discount?.coupon.id || '',
          items: [
            {
              priceId: price.id,
              quantity: 1,
            },
          ],
        })
      ).then(unwrapResult)
      setIsSuccessOpen(true)
    } catch (error) {
      setIsErrorOpen(true)
      console.log('Error while purchasing a subscription', error)
    }
  }

  const handleSuccessPurchase = () => {
    dispatch(invoiceActions.clear())

    if (currentRootId) {
      const homeStatusPath = generatePath(routes.homeStatus, {
        homeId,
        rootDomainId: currentRootId,
      })
      history.push(homeStatusPath)
    }
    setIsSuccessOpen(false)
  }

  const handleCloseErrorModal = () => {
    setIsErrorOpen(false)
  }
  const isDataLoading =
    isPaymentMethodDetaching ||
    isPurchaseRequestLoading ||
    paymentMethodsAreFetching ||
    isDefaultPMRequestLoading ||
    isActiveSubscriptionLoading

  const renewDate = useAppSelector(subscriptionPeriodEndSelector.data)

  const nextBillingDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  ).getTime()

  if (
    paymentMethodsAreFetched &&
    (!paymentMethods || paymentMethods.length === 0)
  ) {
    let redirectPath = ''
    if (currentRootId) {
      redirectPath = generatePath(routes.homeSubscription.addCard(), {
        homeId,
        rootDomainId: currentRootId,
      })
    }
    return <Redirect to={redirectPath} />
  }

  return (
    <StyledUpgradeSubscriptionWrapper>
      <SuccessModal
        title={
          <>
            <PurchaseSuccessIcon path={mdiCheckCircleOutline} size={4} />
            <br />
            {formatMessage(commonMessages.success)}
          </>
        }
        isOpen={isSuccessOpen}
        confirmBtnTitle={formatMessage(commonMessages.close)}
        onConfirm={handleSuccessPurchase}
      >
        {formatMessage(subscriptionManagementMessages.successText, {
          planName: formatMessage(commonMessages.essentialPlan),
          homeName,
          nextBillingDate: formatTime(
            renewDate !== null && renewDate.data !== null
              ? renewDate.data
              : nextBillingDate,
            DATE_SHORT
          ),
          br: <br />,
          bold: (str) => <b>{str}</b>,
        })}
      </SuccessModal>
      <SuccessModal
        title={
          <>
            <PurchaseErrorIcon path={mdiAlert} size={4} />
            <br />
            {formatMessage(subscriptionManagementMessages.errorTitle)}
          </>
        }
        isOpen={isErrorOpen}
        confirmBtnTitle={formatMessage(commonMessages.close)}
        onConfirm={handleCloseErrorModal}
      >
        {formatMessage(subscriptionManagementMessages.errorText)}
      </SuccessModal>
      {isDataLoading && <LoaderOverlay isSemiTransparent />}

      <StyledCardsBlock>
        <StyledPaymentMethods>
          <SectionLabel>
            <span>
              {formatMessage(subscriptionManagementMessages.paymentMethod)}
            </span>
            <CommonLinkButton onClick={addNewCardRedirect}>
              <MdiIcon path={mdiPlus} />
            </CommonLinkButton>
          </SectionLabel>

          <PaymentMethodsCardsList />
        </StyledPaymentMethods>
      </StyledCardsBlock>

      <WhiteBlock>
        <StyledPromocodeRow>
          <SectionLabel>
            <FormattedMessage {...checkoutInfoMessages.title} />
          </SectionLabel>
          {!isOpenPromocodeForm && (
            <StyledAddPromocodeBtn
              variant="secondary"
              size="small"
              onClick={handleOpenPromocodeForm}
            >
              {formatMessage(promocodeMessages.addPromocode)}
            </StyledAddPromocodeBtn>
          )}
        </StyledPromocodeRow>
        {isOpenPromocodeForm && <PromocodeForm priceId={price.id} />}
        <CheckoutInfo
          setIsAcceptPrivacy={setIsAcceptPrivacy}
          setAmountDue={setAmountDue}
        />
      </WhiteBlock>

      <SubsManageBottomPanel
        totalToPayAmount={totalToPayAmount}
        submitLabel={formatMessage(
          subscriptionManagementMessages.checkoutSubmit
        )}
        isSubmitDisabled={isDataLoading || !isAcceptPrivacy}
        handleSubmit={handlePurchaseSubscription}
        handleCancel={handleCancel}
      />
    </StyledUpgradeSubscriptionWrapper>
  )
}

export default UpgradeSubscription
